import { useState } from "react";
import { Mail, Phone } from "lucide-react";
import { Reveal, SectionHead, PageHero } from "@/components/Shared";
import EnquiryForm from "@/components/EnquiryForm";
import { CONTACT_CATS, CONTACTS, EVENT } from "@/data/content";

export default function Contact() {
    const [cat, setCat] = useState(CONTACT_CATS[0]);

    return (
        <div data-testid="contact-page">
            <PageHero
                testid="contact-hero"
                eyebrow="Contact"
                title={<>Let's Build the Bazaar <span className="gold-text">Together.</span></>}
                sub="General, startup, exhibition, investor, partnership and media enquiries — pick a lane and write to us."
            />

            <section className="py-16 md:py-24">
                <div className="container-x grid lg:grid-cols-[1fr_1.3fr] gap-14 items-start">
                    <Reveal>
                        <span className="eyebrow no-lines">Enquiry Lanes</span>
                        <div className="mt-8 space-y-3" role="tablist" aria-label="Enquiry categories">
                            {CONTACT_CATS.map((c) => (
                                <button key={c} onClick={() => setCat(c)} role="tab" aria-selected={cat === c}
                                    data-testid={`contact-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`w-full text-left px-6 py-4 border transition-all duration-400 flex items-center justify-between ${
                                        cat === c
                                            ? "border-[var(--gold)] bg-[rgba(201,162,75,0.06)] text-[var(--champagne)]"
                                            : "hairline text-[var(--ivory)]/70 hover:text-[var(--champagne)] hover:border-[var(--line)]"
                                    }`}>
                                    <span className="text-[0.66rem] tracking-[0.28em] uppercase">{c}</span>
                                    <span className={`w-1.5 h-1.5 rotate-45 ${cat === c ? "bg-[var(--gold)]" : "bg-[var(--muted)]/40"}`} />
                                </button>
                            ))}
                        </div>
                        <div className="mt-10 card-luxe p-7">
                            <p className="text-[0.6rem] tracking-[0.3em] uppercase text-[var(--gold)]">Direct Line</p>
                            <a href={`tel:${EVENT.phone.replace(/\s/g, "")}`} data-testid="contact-phone"
                                className="mt-3 flex items-center gap-3 font-display text-2xl text-[var(--ivory)] hover:text-[var(--champagne)] transition-colors">
                                <Phone size={17} className="text-[var(--gold)]" /> {EVENT.phone}
                            </a>
                            <p className="mt-3 text-xs text-muted-luxe">{EVENT.dates} · {EVENT.venue}</p>
                        </div>
                    </Reveal>
                    <Reveal delay={140}>
                        <EnquiryForm key={cat} defaultCategory={cat} heading={cat} sub="We route every enquiry to the right team lead." testidPrefix="contact-enquiry" />
                    </Reveal>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#1C060C]/60">
                <div className="container-x">
                    <SectionHead eyebrow="Team Leads" title={<>Reach the Right <span className="gold-text">Person Directly</span></>} />
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {CONTACTS.map((c, i) => (
                            <Reveal key={c.email} delay={(i % 3) * 70}>
                                <div className="card-luxe p-7 h-full" data-testid={`contact-lead-${i}`}>
                                    <div className="card-glow" />
                                    <div className="w-10 h-10 arch-top border border-[var(--line)] flex items-center justify-center font-display gold-text text-lg">
                                        {c.name.charAt(0)}
                                    </div>
                                    <h3 className="font-display text-xl mt-5 text-[var(--ivory)]">{c.name}</h3>
                                    <p className="text-[0.6rem] tracking-[0.24em] uppercase text-[var(--gold)] mt-1.5">{c.role}</p>
                                    <p className="text-sm text-muted-luxe mt-3">{c.scope}</p>
                                    <a href={`mailto:${c.email}`} data-testid={`contact-lead-email-${i}`}
                                        className="mt-5 inline-flex items-center gap-2 text-xs text-[var(--champagne)] hover:text-[var(--gold)] transition-colors break-all">
                                        <Mail size={12} className="shrink-0" /> {c.email}
                                    </a>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
